/**
 * BundlerVolumePrice — ticket 86cb0cadb
 *
 * Muestra el precio con descuento por volumen configurado en la app Bundler, con el precio
 * original tachado y un badge de %. Según el contexto:
 *   - Card: "Desde X€/ud" con el MAYOR descuento alcanzable (reclamo del mejor precio).
 *   - PDP: el precio del tramo marcado en el <volume-selector> (1 ud = base; 6/12 = -4/-8%),
 *          SIN "Desde"; cambia al cambiar de tramo. En carrito, el precio aplicado por la
 *          cantidad ya añadida. El €/Kilo (<price-per-type>) se mantiene sincronizado.
 *
 * Los tramos de Bundler solo están disponibles en runtime vía el objeto global
 * `window.bndlr` (no hay metafields server-side), por lo que este componente es
 * un progressive enhancement: la card/PDP renderiza su precio normal y, cuando
 * Bundler está listo, este componente lo sustituye.
 *
 * Data attributes (desde Liquid):
 *   data-product-id   - ID numérico del producto (clave en bundle.products)
 *   data-price        - Precio del producto en céntimos (variante seleccionada)
 *   data-iva          - % de IVA del producto (metafield custom.product_vat)
 *   data-unit-label   - Sufijo de unidad a mostrar (p. ej. "/Und.")
 *   data-label-from   - Traducción de "Desde"
 */
class BundlerVolumePrice extends HTMLElement {
  constructor() {
    super();
    this.maxDiscount = 0;
    // Unidades de este producto en el carrito. > 0 => "modo carrito": se muestra el precio
    // aplicado según la cantidad. Con el carrito vacío, en la PDP se muestra el precio del
    // tramo seleccionado en el <volume-selector>; en la card, el mayor descuento alcanzable.
    this.cartQty = 0;
    // PDP: cantidad (min_items) del tramo marcado en el selector. null = aún no se ha tocado
    // el selector → se usa el tramo preseleccionado (el mayor alcanzable = maxDiscount).
    this.selectedTierQty = null;
    this.onCustomerTypeChange = this.onCustomerTypeChange.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this.onVolumeTierChange = this.onVolumeTierChange.bind(this);
  }

  connectedCallback() {
    // Se leen en connectedCallback (no en el constructor) para soportar tanto el
    // HTML server-rendered como las cards inyectadas dinámicamente.
    this.priceCents = parseInt(this.dataset.price, 10) || 0;
    this.productId = this.dataset.productId;
    this.iva = parseFloat(this.dataset.iva) || 0;
    this.unitLabel = this.dataset.unitLabel || '';
    this.labelFrom = this.dataset.labelFrom || '';

    // Stock disponible del producto. Sin dato de stock asumimos infinito
    // (producto sin gestión de stock).
    this.stock = parseInt(this.dataset.stock, 10);
    if (isNaN(this.stock)) this.stock = Infinity;

    this.cartQty = parseInt(this.dataset.cartQty, 10) || 0;

    if (!this.productId || this.priceCents <= 0) return;

    // Fallback anti-"flash": si el bloque de precio está oculto esperando a Bundler
    // (is-price-pending, ver main-product.liquid) y Bundler no llega a cargar, se revela
    // igualmente pasados unos segundos para no dejar la ficha sin precio. Si el "Desde" se
    // pinta antes, hidePrimaryPrice() ya lo revela.
    const pendingWrap = this.closest('.c-product__price.is-price-pending');
    if (pendingWrap) {
      this.revealTimeout = setTimeout(() => pendingWrap.classList.remove('is-price-pending'), 7000);
    }

    this.getBundlerDiscount();
    document.addEventListener('biribox:customer-type-changed', this.onCustomerTypeChange);
    // El carrito cambia (stepper de la PDP, añadir, quitar) y el tema dispara distintos
    // eventos según el origen: 'cart:refresh' desde el add-to-cart por volumen (main.js) y
    // 'pb:cart:refresh' desde el drawer/mini-cart (botones +/−). Se escuchan AMBOS para que
    // el precio de la PDP se mantenga sincronizado con la cantidad en carrito venga de donde
    // venga. onCartUpdated relee /cart.js y repinta; es idempotente si llegan los dos.
    document.addEventListener('cart:refresh', this.onCartUpdated);
    document.addEventListener('pb:cart:refresh', this.onCartUpdated);
    // PDP: el precio sigue el tramo marcado en el <volume-selector>.
    document.addEventListener('input:volume-tier', this.onVolumeTierChange);
  }

  disconnectedCallback() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    if (this.revealTimeout) {
      clearTimeout(this.revealTimeout);
      this.revealTimeout = null;
    }
    document.removeEventListener('biribox:customer-type-changed', this.onCustomerTypeChange);
    document.removeEventListener('cart:refresh', this.onCartUpdated);
    document.removeEventListener('pb:cart:refresh', this.onCartUpdated);
    document.removeEventListener('input:volume-tier', this.onVolumeTierChange);
  }

  onCustomerTypeChange() {
    if (this.maxDiscount > 0) this.render();
  }

  /**
   * En el carrito el precio se actualiza según la cantidad. Al cambiar el carrito
   * (stepper, añadir, quitar) se relee la cantidad de este producto y se repinta.
   */
  onCartUpdated() {
    if (this.maxDiscount <= 0) return;
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        this.cartQty = (cart.items || [])
          .filter((item) => String(item.product_id) === String(this.productId))
          .reduce((sum, item) => sum + item.quantity, 0);
        this.render();
      })
      .catch(() => {});
  }

  /**
   * PDP: al marcar un tramo en el <volume-selector>, el precio pasa a ser el de ESE tramo
   * (1 ud → base sin descuento; 6 uds → -4%; 12 uds → -8%), y el €/Kilo se sincroniza.
   * Solo aplica fuera del carrito: dentro del carrito manda la cantidad ya añadida.
   */
  onVolumeTierChange(e) {
    // Solo la instancia de la PDP sigue al selector. Una card del mismo producto (p. ej. en
    // "relacionados") debe conservar siempre su "Desde" con el mayor descuento.
    if (!this.closest('.c-product__price')) return;
    if (!e.detail || String(e.detail.productId) !== String(this.productId)) return;
    if (this.maxDiscount <= 0) return;
    // En "modo carrito" el precio lo fija la cantidad en carrito, no el radio.
    if (this.cartQty > 0) return;
    const qty = parseInt(e.detail.quantity, 10);
    if (isNaN(qty)) return;
    this.selectedTierQty = qty;
    this.render();
  }

  /**
   * Descuento a mostrar según el estado:
   *  - En carrito: el aplicado por la cantidad en carrito.
   *  - PDP con un tramo marcado: el de ese tramo.
   *  - Sin interacción: el mayor alcanzable (card, o PDP con el tramo preseleccionado).
   */
  currentDiscount() {
    if (this.cartQty > 0) return this.appliedDiscountForQty(this.cartQty);
    if (this.selectedTierQty != null) return this.appliedDiscountForQty(this.selectedTierQty);
    return this.maxDiscount;
  }

  /**
   * Mayor descuento aplicable para una cantidad en carrito: el del tramo más alto cuyo
   * mínimo de unidades ya se alcanza (p. ej. 6 uds → tramo de 6; 12 uds → tramo de 12).
   */
  appliedDiscountForQty(qty) {
    if (!Array.isArray(this.reachableTiers)) return 0;
    return this.reachableTiers
      .filter((tier) => tier.min_items <= qty)
      .reduce((max, tier) => {
        const value = parseFloat(tier.discount_value) || 0;
        return value > max ? value : max;
      }, 0);
  }

  /**
   * Espera a que Bundler esté disponible (mismo patrón que main.js) y calcula
   * el mayor descuento por volumen del producto.
   */
  getBundlerDiscount() {
    if (this.tryGetDiscount()) return;

    // Bundler (bundler-script.js + su config) puede tardar varios segundos en cargar
    // en un primer acceso sin caché, más que el JS de este componente. Reintentamos
    // mientras Bundler siga cargando, con un tope de seguridad amplio (~30s) para no
    // dejar temporizadores vivos indefinidamente si Bundler nunca llega a cargar.
    let attempts = 0;
    const maxAttempts = 60;
    this.pollInterval = setInterval(() => {
      attempts += 1;
      if (this.tryGetDiscount() || attempts >= maxAttempts) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    }, 500);
  }

  /**
   * Devuelve true cuando la decisión es definitiva (haya que mostrar o no el "Desde")
   * y hay que dejar de reintentar; false si Bundler aún está cargando y hay que esperar.
   */
  tryGetDiscount() {
    if (typeof window.bndlr === 'undefined' || typeof window.bndlr.getBundles !== 'function') {
      return false; // Bundler aún no ha cargado
    }

    const bundles = window.bndlr.getBundles();
    if (!bundles || !bundles.length) return false; // datos aún no disponibles

    // Ordena por prioridad (igual que main.js) y busca el bundle del producto.
    const sorted = [...bundles].sort((a, b) => (a.priority || 999) - (b.priority || 999));
    const matching = sorted.find(bundle => bundle.products && bundle.products[this.productId]);

    if (!matching) {
      // Si Bundler ya terminó de cargar (bundlerLoaded2) y el producto no está en
      // ningún bundle, es definitivo: no hay descuento por volumen. Si todavía está
      // cargando, seguimos esperando (evita ocultar el "Desde" por carrera en el
      // primer load, cuando el bundle del producto aún no ha aparecido en getBundles).
      if (window.bundlerLoaded2 === true) {
        this.revealPendingPrice(); // no hay descuento: revela ya el precio base (no esperes al timeout)
        return true;
      }
      return false;
    }
    if (!Array.isArray(matching.volume_discounts)) {
      this.revealPendingPrice();
      return true;
    }

    // Solo tramos ALCANZABLES con el stock disponible del producto: así el "Desde"
    // no promete un descuento que requiere más unidades de las que hay en stock,
    // pero SÍ se mantiene estable independientemente de lo que el usuario ya tenga
    // en el carrito. El "Desde" es un reclamo informativo del mejor precio por
    // volumen del producto; el filtro por unidades-ya-en-carrito solo corresponde al
    // pop-up (donde se elige cuántas añadir), no a este indicador.
    const reachable = matching.volume_discounts.filter(tier => tier.min_items <= this.stock);

    // El pop-up solo muestra el selector de volumen si sobrevive más de un tramo.
    if (reachable.length <= 1) {
      this.revealPendingPrice();
      return true;
    }

    // Se guardan los tramos alcanzables para poder calcular, en el carrito, el descuento
    // aplicado según la cantidad (appliedDiscountForQty).
    this.reachableTiers = reachable;

    // Mayor descuento entre los tramos alcanzables.
    this.maxDiscount = reachable.reduce((max, tier) => {
      const value = parseFloat(tier.discount_value) || 0;
      return value > max ? value : max;
    }, 0);

    if (this.maxDiscount <= 0) {
      this.revealPendingPrice(); // Sin descuento real alcanzable: no mostramos nada, revela el precio base.
      return true;
    }

    this.render();
    return true;
  }

  /**
   * Replica el redondeo de Shopify en el carrito (Math.ceil al céntimo),
   * igual que getPriceWithoutPercentage() en main.js.
   */
  getDiscountedCents(cents, discount) {
    return Math.ceil(cents - (cents * discount) / 100);
  }

  render() {
    // El descuento a mostrar depende del estado (carrito / tramo seleccionado / preseleccionado).
    const discount = this.currentDiscount();

    // La palabra "Desde" solo se muestra en la CARD (reclamo del mejor precio por volumen).
    // En la PDP el precio sigue el tramo del <volume-selector>, así que NO lleva "Desde".
    const showFrom = !!this.closest('.c-cp__price') && this.cartQty === 0;

    const customerType = localStorage.getItem('ls.biribox.view-type');
    let originalCents = this.priceCents;
    let discountedCents = this.getDiscountedCents(this.priceCents, discount);

    // B2C muestra precios con IVA. Se replica EXACTAMENTE el orden de operaciones
    // de price-box.js (`cents * (iva + 100) / 100`) para que el redondeo coincida:
    // multiplicar primero mantiene la precisión (165*110/100 = 181.5 exacto),
    // mientras que precalcular el multiplicador (165*1.1) introduce error de float
    // y desplaza el .toFixed(2) un céntimo.
    if (customerType === 'private') {
      originalCents = (originalCents * (this.iva + 100)) / 100;
      discountedCents = (discountedCents * (this.iva + 100)) / 100;
    }

    if (discount > 0) {
      // "Desde" solo fuera del carrito; en el carrito se muestra el mismo precio aplicado
      // (rojo + tachado + badge) pero sin la palabra "Desde".
      const fromLabel = showFrom ? `<span class="c-bvp__from">${this.labelFrom}</span>` : '';
      this.innerHTML = `
        <div class="c-bvp">
          ${fromLabel}
          <span class="c-bvp__price">${this.formatEuro(discountedCents)}${this.unitLabel}</span>
          <s class="c-bvp__original">${this.formatEuro(originalCents)}</s>
          <span class="c-badge c-badge--discount">-${discount}%</span>
        </div>
      `;
    } else {
      // Tramo base (sin descuento): se muestra como el precio unitario normal,
      // sin "Desde", sin tachado y sin badge.
      this.innerHTML = `
        <div class="c-bvp c-bvp--plain">
          <span class="c-bvp__price">${this.formatEuro(discountedCents)}${this.unitLabel}</span>
        </div>
      `;
    }

    this.classList.add('is-visible');
    this.hidePrimaryPrice();
    this.syncKiloPrice(discount);
  }

  /**
   * Ajusta el precio por kilo (<price-per-type>) para que coincida SIEMPRE con el precio
   * que muestra el "Desde" (o el aplicado en carrito), en vez de con el tramo resaltado en
   * el selector. Reutiliza el mismo cálculo de descuento e IVA del componente.
   */
  syncKiloPrice(discount) {
    // Empuje: usado en la carga inicial (cuando el bvp renderiza tarde, ya con
    // <price-per-type> inicializado) y al actualizarse el carrito sin re-render. En el
    // re-render de la sección, el orden lo resuelve <price-per-type> tirando de
    // currentPriceCents() (ver PricePerType.connectedCallback).
    const wrap = this.closest('.c-product__price, .c-cp__price');
    const pricePerType = wrap && wrap.querySelector('price-per-type');
    if (!pricePerType || typeof pricePerType.changePriceForType !== 'function') return;

    const businessCents = this.getDiscountedCents(this.priceCents, discount);
    const withIvaCents = (businessCents * (this.iva + 100)) / 100;
    // changePriceForType(precioConIVA, precioSinIVA), ambos en céntimos.
    pricePerType.changePriceForType(withIvaCents, businessCents);
  }

  /**
   * Precio actualmente mostrado (en céntimos: negocio y con IVA), para que <price-per-type>
   * calcule el €/Kilo acorde al precio del "Desde" (o al aplicado en carrito). Devuelve null
   * si aún no hay descuento calculado.
   */
  currentPriceCents() {
    if (this.maxDiscount <= 0 || !this.priceCents) return null;
    const discount = this.currentDiscount();
    const businessCents = this.getDiscountedCents(this.priceCents, discount);
    const withIvaCents = (businessCents * (this.iva + 100)) / 100;
    return { businessCents, withIvaCents };
  }

  /**
   * El precio unitario "normal" es redundante con el "Desde" (que ya lleva el original
   * tachado) y con el precio que se ve en el pop-up / selector, así que se oculta mientras
   * se muestra el "Desde". Cubre las dos ubicaciones:
   *   - Card:  .c-cp__price      → oculta el <price-box> (con la clase global .hidden)
   *   - PDP:   .c-product__price → oculta el .c-product__price__inner (deja el €/Kilo)
   * En ambos casos se añade una clase modificadora al contenedor para el ajuste de
   * espaciado/tamaño (y, en la PDP, el ocultado) vía CSS. Se ejecuta siempre que render()
   * pinta el "Desde"/precio aplicado; solo NO se ejecuta cuando no hay datos de descuento y
   * render() nunca llega a llamarse, dejando el precio unitario visible.
   */
  hidePrimaryPrice() {
    const cardWrap = this.closest('.c-cp__price');
    if (cardWrap) {
      cardWrap.querySelector('price-box')?.classList.add('hidden');
      cardWrap.classList.add('c-cp__price--from-only');
      return;
    }

    // En la PDP el ocultado lo hace la regla CSS scoped a .c-product__price--from-only
    // (la clase global .hidden no gana la cascada en .c-product__price__inner).
    const pdpWrap = this.closest('.c-product__price');
    if (pdpWrap) {
      pdpWrap.classList.add('c-product__price--from-only');
      // Ya está listo el precio por volumen: revela el bloque (ver is-price-pending).
      this.revealPendingPrice();
    }
  }

  /**
   * Quita la clase anti-"flash" (is-price-pending) del bloque de precio de la PDP para
   * revelarlo. Se llama tanto cuando ya se pinta el precio por volumen (hidePrimaryPrice)
   * como cuando se determina que el producto NO tiene descuento por volumen aplicable
   * (en tryGetDiscount): en ese caso el precio base normal debe verse de inmediato, sin
   * esperar al timeout de seguridad. En la card no hay is-price-pending, así que no hace nada.
   */
  revealPendingPrice() {
    const pdpWrap = this.closest('.c-product__price');
    if (pdpWrap) pdpWrap.classList.remove('is-price-pending');
  }

  formatEuro(cents) {
    const value = Number(cents) / 100;
    if (isNaN(value)) return '0,00€';
    return value.toFixed(2).replace('.', ',') + '€';
  }
}

customElements.define('bundler-volume-price', BundlerVolumePrice);
